import { EventFormEvent, EventFormProps, EventFormValues } from "./types";

export interface InitialFormValuesContext {
  tags: EventFormProps["tags"];
  language: string;
  isApplicationEdit: boolean;
  customVenueKeyword: string;
}

const statesPrice = (value: string | null | undefined): boolean =>
  typeof value === "string" && value.trim() !== "";

/**
 * Whether the form opens with "Free event" on: the row states no price in
 * EITHER language (review AD-CR-01). The language split (the desk pre-fill
 * and ApplicationEdit) puts a Portuguese post's price on `price_pt` only, so
 * reading `price` alone opened every priced Portuguese post as free and
 * published it with no price. A manual create (no event) keeps the price
 * inputs shown, as before.
 */
export const isFreeEventOf = (
  event: Pick<EventFormEvent, "price" | "price_pt"> | undefined
): boolean => {
  // An absent `price` key (not null, not "") is "unknown", never "free": the
  // same reading the old `price === null || price === ""` rule gave it.
  if (!event || event.price === undefined) return false;
  return !statesPrice(event.price) && !statesPrice(event.price_pt);
};

/**
 * The form's start state as a pure function of the event row.
 *
 * Lifted verbatim out of EventFormMain's `useForm({ initialValues })` so that
 * the desk can compute the very same values as its baseline (DESK-07, D-09):
 * the fields she edited are the ones whose submitted value differs from what
 * this function returned for the row she was shown. One transform, so the
 * baseline can never drift from what the form actually opened with.
 */
export const initialFormValues = (
  event: EventFormEvent | undefined,
  ctx: InitialFormValuesContext
): EventFormValues => {
  const { tags, language, isApplicationEdit, customVenueKeyword } = ctx;

  return {
    title: event?.title || "",
    title_pt: event?.title_pt || "",
    subTitle: event?.sub_title || "",
    subTitle_pt: event?.sub_title_pt || "",
    user: event?.organizer_id || null,
    description: event?.description || "",
    description_pt: event?.description_pt || "",
    picture: event?.picture || "",
    // FBIMP-12: an imported application opens with an EMPTY date when the
    // source never stated one. A defaulted control is indistinguishable from
    // a curated one at review time, which is exactly what the upstream
    // null-preservation work exists to prevent. The today-at-10:00 and
    // today-at-12:00 convenience defaults survive for MANUAL event creation
    // only — do not restore them on the application path.
    startTime: event?.start_time
      ? new Date(event.start_time)
      : isApplicationEdit ? null : new Date(new Date().setHours(10, 0, 0, 0)),
    finishTime: event?.finish_time
      ? new Date(event.finish_time)
      : isApplicationEdit ? null : new Date(new Date().setHours(12, 0, 0, 0)),
    price: event?.price || "",
    price_pt: event?.price_pt || "",
    mainTag: event?.main_tag
      ? (() => {
          const selectedTag = tags.find((tag) => tag.id === event?.main_tag);
          return language === "pt-PT"
            ? selectedTag?.tag_pt || selectedTag?.tag || null
            : selectedTag?.tag || null;
        })()
      : null,
    registrationLink: event?.registration_link || "",
    venue: event?.venues?.name || customVenueKeyword,
    venue_name: event?.venue_name || "",
    venue_address: event?.venue_address || "",
    venue_city: event?.venue_city || "",
    city_id: event?.city_id ?? null,
    venue_description: event?.venue_description || "",
    venue_location: event?.venue_location_lat
      ? `${event?.venue_location_lat},${event?.venue_location_long}`
      : "",
    published: event?.published || false,
    cancelled: event?.cancelled || false,
    isRecommended: event?.is_recommended || false,
    isFreeEvent: isFreeEventOf(event),
  };
};
